import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { showMessage } from '../../components/components/SnackbarNotification/snackbarNotificationSlice';
import i18n from 'src/i18n';

export interface ExcelReportDto {
    fileName: string;
    blob: Blob;
}

export interface ReportDto {
    name: string;
}

export interface GenerateReportDto extends ReportDto {
    referenceYear: number;
}

export interface ReportState {
    reports: ReportDto[];
    report: ReportDto | undefined;
    gettingReports: boolean;
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    error?: string;
}

const initialState: ReportState = {
    reports: [],
    report: undefined,
    gettingReports: false,
    status: 'idle',
    error: undefined,
};

export const reportSlice = createSlice({
    name: 'reports',
    initialState,
    reducers: {
        clearReports: (state) => {
            state.reports = [];
            state.report = undefined;
        },
        setSelectedReportType: (state, param) => {
            const { payload } = param;
            state.report = payload;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(getReports.pending, (state, action) => {
                state.gettingReports = true;
                state.status = 'loading';
            })
            .addCase(getReports.fulfilled, (state, action) => {
                state.gettingReports = false;
                state.reports = action.payload;
                state.status = 'succeeded';
            })
            .addCase(getReports.rejected, (state, action) => {
                state.gettingReports = false;
                state.reports = [];
                state.status = 'failed';
                state.error = action.error.message;
            })
            .addCase(generateReport.pending, (state, action) => {
                state.gettingReports = true;
                state.status = 'loading';
            })
            .addCase(generateReport.fulfilled, (state, action) => {
                state.gettingReports = false;
                state.status = 'succeeded';
            })
            .addCase(generateReport.rejected, (state, action) => {
                state.gettingReports = false;
                state.status = 'failed';
                state.error = action.error.message;
            });
    },
});

export const { clearReports, setSelectedReportType } = reportSlice.actions;

export const getReports = createAsyncThunk('reports/getReports', async (_, thunkAPI) => {
    const response = await axios.get(`/api/synergy/reports`);

    return response.data;
});

export const generateReport = createAsyncThunk('reports/generateReport', async (reportToGenerate: GenerateReportDto, thunkAPI): Promise<ExcelReportDto | undefined> => {
    return axios.post(`/api/synergy/reports/${reportToGenerate.referenceYear}`, reportToGenerate, { responseType: "blob" })        
        .then((response) => {
            const disposition = response.headers["content-disposition"];
            let fileName = "report.xlsx";

            if (disposition) {
                const match = disposition.match(/filename="?([^"]+)"?/);
                if (match && match[1]) {
                    fileName = match[1];
                }
            }

            const blob = new Blob([response.data], {
                type: response.headers["content-type"]?.toString() ?? 'application/octet-stream'
            });

            return {
                blob: blob,
                fileName: fileName
            };
        })
        .catch(async (error) => {
            const message = await error.response.data.text();
            thunkAPI.dispatch(
                showMessage({
                    type: 'error',
                    message:  i18n.t(message),
                }));
            
            return undefined;
        });
});
